import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { routes } from '@/utils/routes'

export type IntegrationsTabKey = 'connections' | 'inbound' | 'outbound'

export const INTEGRATIONS_TABS: Array<{ key: IntegrationsTabKey; label: string }> = [
    { key: 'connections', label: 'Connections' },
    { key: 'inbound', label: 'Inbound webhooks' },
    { key: 'outbound', label: 'Outbound webhooks' },
]

const DEFAULT_TAB: IntegrationsTabKey = 'connections'

const isTabKey = (value: string | null): value is IntegrationsTabKey =>
    value === 'connections' || value === 'inbound' || value === 'outbound'

/**
 * Which Integrations tab is showing, kept in the address bar.
 *
 * A query parameter rather than a path segment, unlike the Web tabs: settings sections are a flat
 * route table keyed on the exact path, and a segment would mean teaching that table about a child
 * route for one section's benefit. The addressability is what matters here, and this is the cheaper
 * half of it — someone can send "here is your outbound webhooks screen" as a link, which is the
 * thing that was missing when the outbound panel was a card at the bottom of a scroll.
 */
export function useIntegrationsTabRouter(companyId: string): {
    activeTab: IntegrationsTabKey
    routeToTab: (tab: IntegrationsTabKey) => void
} {
    const location = useLocation()
    const [activeTab, setActiveTab] = useState<IntegrationsTabKey>(() => readTabFromUrl())

    // preact-iso re-renders on navigation but does not expose the query string consistently across
    // versions, so the URL itself is the source of truth and location is only the change signal.
    useEffect(() => {
        const fromUrl = readTabFromUrl()
        if (fromUrl !== activeTab) setActiveTab(fromUrl)
    }, [location])

    const routeToTab = (tab: IntegrationsTabKey) => {
        setActiveTab(tab)
        const base = routes.settingsIntegrations(companyId)

        // Rewrite only the tab key. Building the string as `${base}?tab=` would drop every other
        // parameter on the page — there are none today, but a URL helper that discards query state
        // is a trap laid for whoever adds the first one.
        const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search)
        if (tab === DEFAULT_TAB) params.delete('tab')
        else params.set('tab', tab)

        const query = params.toString()
        const target = query ? `${base}?${query}` : base
        try {
            location.route(target)
        } catch {
            if (typeof window !== 'undefined') window.history.pushState({}, '', target)
        }
    }

    return { activeTab, routeToTab }
}

function readTabFromUrl(): IntegrationsTabKey {
    if (typeof window === 'undefined') return DEFAULT_TAB
    const value = new URLSearchParams(window.location.search).get('tab')
    return isTabKey(value) ? value : DEFAULT_TAB
}
